import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIResource } from "../../constants/application";
import { getUrl } from "utils/getURL";
import axiosInstance from "utils/axiosInstance";
import { AssociatedCustomerDataList } from "./validatePAN";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import { AppSpecificName } from "constants/headerImage";

export interface IValidateBankAPIData {
  customerId: string;
  bankAccountNumber: string;
  ifscCode: string;
  userProvidedName: string | null;
  accountType: string;
  customerSource: string;
  accountProofDocumentUrl?:string;
}

interface IBankResponse {
  accountType?: string;
  bankAccountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  branchName?: string;
  bankAddress?: string;
  accountHolderName?: string;
  userNameMatchScore?: number;
  associatedCustomerDataList?: AssociatedCustomerDataList[];
  accountProofDocumentUrl?: string;
}

export interface IBankSlice {
  loading: boolean;
  error: string;
  success: boolean;
  bankResponse: null | IBankResponse;
  bankStatus: (typeof BANK_STATUS_VARIANTS)[keyof typeof BANK_STATUS_VARIANTS];
}

const defaultValues: IBankSlice = {
  loading: false,
  error: "",
  success: false,
  bankResponse: null,
  bankStatus: BANK_STATUS_VARIANTS.PENDING,
};

interface IValidateBank {
  data: IValidateBankAPIData;
}
// Async thunk for Bank validation
export const ValidateBank = createAsyncThunk(
  "validateBank",
  async ({ data }: IValidateBank, thunkAPI) => {
    const { dispatch } = thunkAPI; // Access dispatch through thunkAPI
    try {
      const appName:any = localStorage.getItem('appname');
      data['customerSource'] = AppSpecificName[appName];


      const response = await axiosInstance.post(
        getUrl(APIResource.validateBank, [
          {
            variableName: "customerId",
            variableValue: data.customerId,
          },
        ]),
        {
          ...data,
        }
      );
      if (response.status === 200) {
        if (response?.data?.message) {
          dispatch(setBankResponse({ value: null }));
          dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
          if (
            response?.data?.error == "KYC-101" ||
            response?.data?.error == "KYC-304"
          ) {
            return thunkAPI.rejectWithValue(
              "We couldn’t find a bank account matching these details. Please check and try again."
            );
          } else if (response.data?.error == "KYC-105") {
            return thunkAPI.rejectWithValue(
              "Daily verification limit exceeded. Please try again after 24hrs."
            );
          } else {
            return thunkAPI.rejectWithValue(response?.data?.message as string);
          }
        } else if (response.data && response.data.associatedCustomerDataList && response.data.associatedCustomerDataList.length>0) {
          dispatch(
            setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_ASSOCIATION })
          );
          dispatch(setBankResponse({ value: response.data }));
        } else if (response?.data?.accountType) {
          dispatch(
            setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED })
          );
          dispatch(setBankResponse({ value: response.data }));
        } else {
          dispatch(
            setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_INACTIVE })
          );
          dispatch(setBankResponse({ value: null }));
          return thunkAPI.rejectWithValue(
            "Error Occurred!, Please contact Bayer customer support"
          );
        }
      } else {
        dispatch(setBankResponse({ value: null }));
        dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
        return thunkAPI.rejectWithValue(
          "Error Occurred!, Please contact Bayer customer support"
        );
      }
    } catch (error: any) {
      dispatch(setBankResponse({ value: null }));
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
      if (error?.response?.data?.message) {
        if (
          error?.response?.data?.error == "KYC-101" ||
          error?.response?.data?.error == "KYC-304"
        ) {
          return thunkAPI.rejectWithValue(
            "We couldn’t find a bank account matching these details. Please check and try again."
          );
        } else if (error?.response?.data?.error == "KYC-105") {
          return thunkAPI.rejectWithValue(
            "Daily verification limit exceeded. Please try again after 24hrs."
          );
        } else {
          return thunkAPI.rejectWithValue(
            error?.response?.data?.message as string
          );
        }
      } else {
        return thunkAPI.rejectWithValue(
          "Something went wrong, Please contact Bayer customer support"
        );
      }
    }
  }
);

// Slice definition
const BankSlice = createSlice({
  name: "validateBank",
  initialState: defaultValues,
  reducers: {
    setBankStatus: (state, action) => {
      state.bankStatus = action.payload.value;
    },
    setBankResponse: (state, action) => {
      state.bankResponse = action.payload.value;
    },
    setError: (state, action) => {
      state.error = action.payload.value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ValidateBank.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(ValidateBank.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
        state.success = true;
      })
      .addCase(ValidateBank.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { setError, setBankResponse, setBankStatus } = BankSlice.actions;
export default BankSlice.reducer;
